/**
 * MarsMate — Dashboard Stats Widget (Component 4)
 *
 * npm install gsap recharts @lottiefiles/react-lottie-player
 *
 * Features:
 *   ✓ Animated number counter on viewport entry (GSAP)
 *   ✓ Sparkline chart with draw-in animation (Recharts)
 *   ✓ Loading skeleton shimmer state
 *   ✓ Success/change indicator badge
 *   ✓ Glassmorphism card
 *
 * Usage:
 *   import StatsWidget, { StatsGrid } from '../components/upgraded/StatsWidget';
 *
 *   // Single widget:
 *   <StatsWidget
 *     label="Ideas Validated"
 *     value={1247}
 *     change={+12.5}
 *     suffix=""
 *     prefix=""
 *     sparkData={[40,55,30,70,65,80,95]}
 *     color="#C1440E"
 *     loading={false}
 *   />
 *
 *   // Dashboard grid of 4:
 *   <StatsGrid stats={myStats} loading={loading} />
 */

import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  AreaChart, Area, ResponsiveContainer, Tooltip,
} from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';
import { DURATION, EASE, SPRING, COLORS } from '../../animations/timing';

gsap.registerPlugin(ScrollTrigger);

// ─────────────────────────────────────────────
// Skeleton shimmer
// ─────────────────────────────────────────────
function Skeleton({ width = '100%', height = 16, radius = 6, style = {} }) {
  return (
    <div style={{
      width, height, borderRadius: radius,
      background: 'linear-gradient(90deg, #161B27 0%, #1E2535 50%, #161B27 100%)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.6s ease-in-out infinite',
      ...style,
    }} />
  );
}

// ─────────────────────────────────────────────
// AnimatedCounter — GSAP number counter
// ─────────────────────────────────────────────
function AnimatedCounter({ value, prefix = '', suffix = '', decimals = 0, color = '#F1F5F9' }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(0);
  const triggered = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const trigger = ScrollTrigger.create({
      trigger: el,
      start: 'top 90%',
      once: true,
      onEnter: () => {
        if (triggered.current) return;
        triggered.current = true;
        gsap.to({ val: 0 }, {
          val: value,
          duration: 1.8,
          ease: 'power3.out',
          onUpdate: function () {
            setDisplay(
              decimals > 0
                ? parseFloat(this.targets()[0].val.toFixed(decimals))
                : Math.round(this.targets()[0].val)
            );
          },
        });
      },
    });

    return () => trigger.kill();
  }, [value, decimals]);

  const formatted = typeof display === 'number'
    ? display >= 1000
      ? `${(display / 1000).toFixed(1)}K`
      : display.toLocaleString()
    : display;

  return (
    <span ref={ref} style={{
      fontSize: 34, fontWeight: 800,
      fontFamily: 'Plus Jakarta Sans, sans-serif',
      color,
      letterSpacing: '-0.03em',
      background: `linear-gradient(135deg, ${color}, ${color}bb)`,
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    }}>
      {prefix}{formatted}{suffix}
    </span>
  );
}

// ─────────────────────────────────────────────
// ChangeBadge — green/red indicator
// ─────────────────────────────────────────────
function ChangeBadge({ change }) {
  if (change == null) return null;
  const isPositive = change >= 0;
  const color = isPositive ? '#22C55E' : '#EF4444';
  const bg    = isPositive ? 'rgba(34,197,94,0.10)' : 'rgba(239,68,68,0.10)';
  const icon  = isPositive ? '↑' : '↓';

  return (
    <motion.span
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={SPRING.bouncy}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 3,
        padding: '3px 8px', borderRadius: 6,
        background: bg, color,
        fontSize: 12, fontWeight: 700,
        fontFamily: 'DM Sans, sans-serif',
        border: `1px solid ${color}25`,
      }}
    >
      {icon} {Math.abs(change)}%
    </motion.span>
  );
}

// ─────────────────────────────────────────────
// MiniSparkline — Recharts draw-in area chart
// ─────────────────────────────────────────────
function MiniSparkline({ data = [], color = '#C1440E' }) {
  const chartData = data.map((v, i) => ({ i, v }));

  return (
    <ResponsiveContainer width="100%" height={52}>
      <AreaChart data={chartData} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id={`grad-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.35} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Tooltip
          content={() => null}
          cursor={false}
        />
        <Area
          type="monotone"
          dataKey="v"
          stroke={color}
          strokeWidth={2}
          fill={`url(#grad-${color.replace('#', '')})`}
          isAnimationActive
          animationBegin={400}
          animationDuration={1200}
          animationEasing="ease-out"
          dot={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ─────────────────────────────────────────────
// StatsWidget — main component
// ─────────────────────────────────────────────
export default function StatsWidget({
  label        = 'Total Users',
  value        = 0,
  change       = null,
  prefix       = '',
  suffix       = '',
  decimals     = 0,
  sparkData    = [20, 40, 35, 55, 45, 70, 65, 80],
  color        = '#C1440E',
  loading      = false,
  icon         = null,
  subLabel     = null,
}) {
  return (
    <motion.div
      whileHover={{
        y: -3,
        boxShadow: `0 20px 50px rgba(0,0,0,0.4), 0 0 30px ${color}12`,
        borderColor: `${color}30`,
      }}
      transition={{ duration: DURATION.fast, ease: EASE.spring }}
      style={{
        background: COLORS.bg.glass,
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: `1px solid ${COLORS.border.default}`,
        borderRadius: 16,
        padding: '22px 24px 18px',
        position: 'relative',
        overflow: 'hidden',
        minWidth: 200,
      }}
    >
      {/* Top accent line */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        height: 2,
        background: `linear-gradient(90deg, ${color}, ${color}00)`,
      }} />

      {loading ? (
        // ── Skeleton state ──
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Skeleton width="55%" height={14} />
          <Skeleton width="70%" height={34} />
          <Skeleton width="100%" height={52} radius={4} style={{ marginTop: 4 }} />
          <Skeleton width="40%" height={12} />
        </div>
      ) : (
        // ── Loaded state ──
        <>
          {/* Header row */}
          <div style={{
            display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', marginBottom: 12,
          }}>
            <span style={{
              color: '#94A3B8', fontSize: 13, fontWeight: 500,
              fontFamily: 'DM Sans, sans-serif',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              {icon && <span style={{ fontSize: 16 }}>{icon}</span>}
              {label}
            </span>
            <ChangeBadge change={change} />
          </div>

          {/* Counter */}
          <AnimatedCounter
            value={value}
            prefix={prefix}
            suffix={suffix}
            decimals={decimals}
            color={color}
          />

          {/* Sub-label */}
          {subLabel && (
            <p style={{
              color: '#64748B', fontSize: 12,
              fontFamily: 'DM Sans, sans-serif',
              margin: '4px 0 0',
            }}>
              {subLabel}
            </p>
          )}

          {/* Sparkline */}
          <div style={{ marginTop: 14 }}>
            <MiniSparkline data={sparkData} color={color} />
          </div>
        </>
      )}

      {/* shimmer keyframe */}
      <style>{`
        @keyframes shimmer {
          0%   { background-position: -200% 0; }
          100% { background-position:  200% 0; }
        }
      `}</style>
    </motion.div>
  );
}

// ─────────────────────────────────────────────
// StatsGrid — 4-up dashboard layout
// ─────────────────────────────────────────────
const DEFAULT_STATS = [
  {
    id: 's1', label: 'Ideas Validated',
    value: 1247, change: 12.5,
    sparkData: [30, 42, 38, 55, 48, 72, 68, 85],
    color: '#C1440E', icon: '💡', suffix: '',
    subLabel: 'All time',
  },
  {
    id: 's2', label: 'AI Reports Generated',
    value: 3891, change: 8.2,
    sparkData: [20, 35, 28, 50, 42, 65, 58, 78],
    color: '#E5622D', icon: '📊', suffix: '',
    subLabel: 'Since signup',
  },
  {
    id: 's3', label: 'Mentor Sessions',
    value: 48, change: -3.1,
    sparkData: [8, 12, 10, 16, 12, 9, 14, 11],
    color: '#F5A623', icon: '👥', suffix: '',
    subLabel: 'This month',
  },
  {
    id: 's4', label: 'Validation Score',
    value: 87, change: 5.0,
    sparkData: [60, 65, 70, 68, 74, 78, 82, 87],
    color: '#10B981', icon: '🎯', suffix: '%',
    subLabel: 'Latest idea',
  },
];

export function StatsGrid({ stats = DEFAULT_STATS, loading = false }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
      gap: 16,
    }}>
      {stats.map((stat, i) => (
        <motion.div
          key={stat.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: DURATION.normal,
            ease: EASE.spring,
            delay: i * 0.08,
          }}
        >
          <StatsWidget {...stat} loading={loading} />
        </motion.div>
      ))}
    </div>
  );
}
